import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnMeta } from '@/types/dataset';
import { usePinnedFiltersStore } from '@/store/pinned-filters-store';
import { kindOf } from '../column-types';

type Props = {
  columns: readonly ColumnMeta[];
  pinned: readonly string[];
  onClose: () => void;
};

/**
 * Inline form rendered inside the sidebar 'filter' sub-panel — lets
 * the user pick which columns appear as persistent slots in
 * {@link PinnedFilterBar}. Changes persist to IndexedDB on save.
 */
export function ManagePinnedFiltersForm({
  columns,
  pinned,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const setColumns = usePinnedFiltersStore((s) => s.setColumns);
  const [draft, setDraft] = useState<string[]>(() => [...pinned]);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return columns;
    return columns.filter((c) => c.name.toLowerCase().includes(q));
  }, [columns, search]);

  function toggle(name: string): void {
    setDraft((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  }

  function move(name: string, delta: -1 | 1): void {
    setDraft((prev) => {
      const i = prev.indexOf(name);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  }

  async function save(): Promise<void> {
    await setColumns(draft);
    onClose();
  }

  return (
    <div className="flex flex-col gap-3" style={{ color: 'var(--color-fg)' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('common.searchColumn')}
          className="text-xs p-1.5 rounded"
          style={{ border: '1px solid var(--color-border)' }}
          autoFocus
        />

        {draft.length > 0 && (
          <div className="flex flex-col gap-1">
            <span
              className="text-[11px] uppercase tracking-wide"
              style={{ color: 'var(--color-muted)' }}
            >
              {t('pinnedFilters.manage.sortingHeader')}
            </span>
            <div
              className="flex flex-col gap-0.5 p-1 rounded"
              style={{ border: '1px solid var(--color-border)' }}
            >
              {draft.map((name, i) => (
                <div
                  key={name}
                  className="flex items-center gap-2 px-1.5 py-0.5 text-xs rounded"
                  style={{ color: 'var(--color-fg)' }}
                >
                  <span className="flex-1 truncate" title={name}>
                    {name}
                  </span>
                  <button
                    type="button"
                    onClick={() => move(name, -1)}
                    disabled={i === 0}
                    aria-label={t('pinnedFilters.manage.moveUpAria', { name })}
                    className="text-[10px] px-1 disabled:opacity-40"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => move(name, 1)}
                    disabled={i === draft.length - 1}
                    aria-label={t('pinnedFilters.manage.moveDownAria', { name })}
                    className="text-[10px] px-1 disabled:opacity-40"
                  >
                    ▼
                  </button>
                  <button
                    type="button"
                    onClick={() => toggle(name)}
                    aria-label={t('pinnedFilters.manage.removeAria', { name })}
                    className="text-[10px] px-1"
                    style={{ color: 'var(--color-danger)' }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1 min-h-0 flex-1">
          <span
            className="text-[11px] uppercase tracking-wide"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('pinnedFilters.manage.availableHeader')}
          </span>
          <div
            className="flex flex-col overflow-auto min-h-[160px] max-h-[260px] p-1 rounded"
            style={{ border: '1px solid var(--color-border)' }}
          >
            {filtered.length === 0 && (
              <div
                className="text-xs p-2"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('common.noMatch')}
              </div>
            )}
            {filtered.map((c) => {
              const isOn = draft.includes(c.name);
              return (
                <label
                  key={c.name}
                  className="flex items-center gap-2 px-1.5 py-0.5 text-xs cursor-pointer hover:opacity-80"
                  style={{ color: 'var(--color-fg)' }}
                >
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={() => toggle(c.name)}
                  />
                  <span className="flex-1 truncate" title={c.name}>
                    {c.name}
                  </span>
                  <span
                    className="text-[10px] uppercase"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {kindOf(c.sqlType)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1 rounded"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => {
            void save();
          }}
          className="text-xs px-3 py-1 rounded text-white"
          style={{ background: 'var(--color-accent)' }}
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}
